import { useTranslation } from 'react-i18next'
import { Icon } from '@/components/forge/Icon'
import { Num } from '@/components/forge/primitives'

/**
 * What a MEDIA record is linked to.
 *
 * ── Why this has a file of its own now ───────────────────────────────────
 * It lived in `DocumentsPage` and was shared by the two registers, back when
 * documents and media were the same demo collection with the same three link
 * kinds. Documents went live and its links became `DocumentAttachment` — five
 * entity types, named `plant`/`line`/`machine`/`asset_class`/`sensor`, and
 * carried only on the detail read. The register's own version of this is
 * the drawer's own attachment list, which is a row per attachment rather than
 * a chip strip — five kinds do not fit on one line of a table cell.
 *
 * Media is still DEMO and still has `{kind: 'asset'|'line'|'site', id, name}`,
 * so it keeps this one unchanged. Leaving it in `DocumentsPage` would have made
 * the live register export a component only the fixture screen uses, and would
 * have pulled the whole live module into Media's graph.
 *
 * Two, then a count. A photograph of one machine has one chip; nothing has more
 * than three, and a row that grew to six would push every column after it off
 * the screen.
 */
export function LinkChips({ links, max = 2 }) {
  const { t } = useTranslation('app')

  if (!links.length) {
    return (
      <span className="text-micro font-normal text-ink-mute italic">
        {t('dataFusion.docs.notLinked')}
      </span>
    )
  }

  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      {links.slice(0, max).map((link) => (
        <span
          key={`${link.kind}-${link.id}`}
          className="inline-flex items-center gap-1 rounded-pill bg-sunken px-2 py-[2px] text-micro font-normal whitespace-nowrap text-ink-dim"
        >
          <Icon name={LINK_ICON[link.kind]} size="xs" className="text-ink-mute" />
          {link.name}
        </span>
      ))}
      {links.length > max ? (
        <Num className="text-micro text-ink-mute">
          {t('dataFusion.docs.moreLinks', { count: links.length - max })}
        </Num>
      ) : null}
    </span>
  )
}

/** The demo's three link kinds. The live register's five are in `KIND_ICON`. */
const LINK_ICON = { asset: 'factory', line: 'gauge', site: 'map' }
